import { useEffect, useRef } from 'react';
import { useClerk } from '@clerk/clerk-react';
import {
  trackSignupAttemptCreated,
  trackSignupEmailVerificationFailed,
  trackSignupEmailVerificationPending,
} from '../lib/posthog';

/**
 * Emits PostHog events for progress inside Clerk's hosted sign-up UI.
 * Must render inside <ClerkProvider>.
 *
 * Clerk is a black box between a sign-up CTA click and `signup_completed`
 * (fired by ClerkIdentify). Its flow stacks a Turnstile bot check, the
 * account form, and an email-code verification — any of which can silently
 * eat a prospect. Listening to the client's SignUp resource makes each
 * hop a funnel step (see event map in lib/posthog.ts).
 */
export function ClerkSignupTelemetry() {
  const clerk = useClerk();
  const seenAttemptId = useRef<string | null>(null);
  const seenEmailState = useRef<string | null>(null);

  useEffect(() => {
    return clerk.addListener(({ client }) => {
      const signUp = client?.signUp;
      if (!signUp?.id) return;

      if (seenAttemptId.current !== signUp.id) {
        seenAttemptId.current = signUp.id;
        trackSignupAttemptCreated({
          has_email: Boolean(signUp.emailAddress),
          oauth_strategy: signUp.verifications?.externalAccount?.strategy ?? null,
        });
      }

      const emailStatus = signUp.verifications?.emailAddress?.status ?? null;
      if (!emailStatus) return;
      const emailState = `${signUp.id}:${emailStatus}`;
      if (seenEmailState.current === emailState) return;
      seenEmailState.current = emailState;

      if (emailStatus === 'unverified') {
        trackSignupEmailVerificationPending();
      } else if (emailStatus === 'failed' || emailStatus === 'expired') {
        trackSignupEmailVerificationFailed({ verification_status: emailStatus });
      }
    });
  }, [clerk]);

  return null;
}
